// 首頁「事件類別統計」的分群與計數（純函式；元件在 HomeEventStats.tsx）。
//
// 2026-08-20 業主裁決：首頁的「事件時間軸」換成**事件類別統計**。理由是兩位實際使用者
// (albert/bo) 獨立反映首頁「怪」——診斷是首頁沒有自己的東西，每一塊都是別的 Tab 的清單
// 複製（裝置軸↔攝影機 Tab、事件時間軸↔事件 Tab）。統計回答的是「這一週家裡發生了幾次
// 什麼」，那是清單答不出來、而且只有首頁該回答的問題。
//
// ⚠️ **分群不自己發明**：型別判定一律走 `eventsLib.clusterOf`（= `common/01 §1.2`
// event_id bits[39:24] 的鏡像，與 iOS `HomeEventCluster` / Android `LSEvents.clusterOf`
// 同一張表）。本檔**唯一**新增的判準是「哪些 cluster 屬於維運/稽核（系統）」——web 的
// `ClusterKey` 沒有 `system` 這一格，系統事件與 tamper 一起落在 `other`，光靠
// `clusterOf` 分不出來。
//
// 為什麼不直接把 `system` 加進 `eventsLib.CLUSTER_DEFS`：那張表是三平台共用的 SSOT 鏡像，
// 動它會連帶改變**事件頁**的型別 chip 與 `MemoriesPage` 的精選判定（web 事件頁的「全部類型」
// 目前**含**系統列，沒有 system chip 可以把它們找回來——改了就等於讓那些列變成不可達）。
// Android 遇到同一個問題時的作法也是在畫面層另立 `EventDisplayKind`，不動 lscore 的
// `EventCluster`（見 `EventLabelsUi.kt` 的同段註解）；iOS 的 `HomeEventCluster` 同樣住在
// 畫面層（`Events.swift`）而不是 LSCoreKit。本檔就是 web 的對應位置。

import { clusterOfEvent, type ClusterKey, type EventItem } from '../events/eventsLib';

/** 統計格。與 `ClusterKey` 同值域——系統事件不佔格（見 `homeStatKeyOf`）。 */
export type HomeEventStatKey = ClusterKey;

/**
 * 固定顯示順序。**含計數 0 的群也要畫**：0 有資訊量（「這週沒有門鈴」），而且版面不會
 * 隨當批資料跳動。⚠️ 不要改成「只顯示有資料的群」——那讓資訊架構由當批資料決定，
 * 使用者建立不了肌肉記憶（同 `Events.swift` 分群註解與 `EVENT_FILTER_ORDER` 的理由）。
 *
 * `other` 收的是**非系統、但已知 cluster 都不是**的事件——實際內容是鏡頭遭遮蔽
 * (0x8044)、`0x8041` 未登記子序號（`object`），與型別表還沒收錄的安全動態。它必須在：
 * 少了它，一批只有 tamper 的資料會讓首頁各格全 0，而事件頁點開有列——正是本輪要修掉的
 * 那種「首頁說謊」。
 */
export const HOME_EVENT_STAT_ORDER: readonly HomeEventStatKey[] = [
  'motion',
  // 物件辨識四子類（業主 2026-08-21 裁決）。原本 `0x8041` 整群一格叫「人形」，
  // dev DDB 實資料對帳：1300 筆「人形」裡有 39 筆車 + 57 筆動物。
  'person',
  'vehicle',
  'animal',
  'package',
  'sound',
  'doorbell',
  'other',
] as const;

/**
 * 統計格**不含**這些 `ClusterKey`：它們沒有自己的格，一律併進 `other`。
 * `object` = 認得是 0x8041 但子序號不在規格四類裡。**不能丟掉**：丟掉的話各格相加 < 總數。
 */
const FOLDED_INTO_OTHER: ReadonlySet<ClusterKey> = new Set<ClusterKey>(['object']);

/**
 * 維運 / 稽核面的 cluster bits[39:24]。逐項鏡像 iOS `Events.swift` 的 `eventClusterByBits`
 * 中標為 `.system` 的那一段（Device Health / Recording / PTZ / SD card / Wi-Fi / Battery）。
 * ⚠️ 0x8044（鏡頭遭遮蔽）**不在**這裡：它是安全事件，不是維運紀錄。
 */
const SYSTEM_CLUSTER_BITS: ReadonlySet<number> = new Set([
  0x8000, // Device Health：上下線、韌體更新、訂閱到期、綁定/解綁、連線品質
  0x8050, // Recording：開始/結束/中斷/片段儲存
  0x8060, // PTZ：鏡頭移動、自動追蹤起停
  0x8090, // SD card：插入/移除/異常
  0x80c0, // Wi-Fi：已更換/更換失敗
  0x80d0, // Battery：電量回報、喚醒/休眠
]);

/** 非 hex 的 `event_type`（restful ch19 語意字串）裡屬稽核紀錄的。鏡像 iOS `eventClusterByType`。 */
const SYSTEM_COARSE_TYPES: ReadonlySet<string> = new Set(['device_unbound']);

const HEX_ID = /^0[xX][0-9a-fA-F]+$/;

/** 這一筆是不是維運/稽核紀錄（＝首頁統計不計入的那些）。 */
export function isSystemEvent(ev: Pick<EventItem, 'event_id' | 'event_type'>): boolean {
  const id = ev.event_id;
  if (typeof id === 'string' && HEX_ID.test(id)) {
    try {
      const bits = Number((BigInt(id) >> 24n) & 0xffffn);
      return SYSTEM_CLUSTER_BITS.has(bits);
    } catch {
      // 解析不出來就不是已知的系統群——當成一般事件計進 `other`，不要偷偷吞掉。
      return false;
    }
  }
  return SYSTEM_COARSE_TYPES.has(ev.event_type ?? '');
}

/** 這一筆該計進哪一格；`null` = 系統紀錄，不計入。 */
export function homeStatKeyOf(ev: Pick<EventItem, 'event_id' | 'event_type'>): HomeEventStatKey | null {
  if (isSystemEvent(ev)) return null;
  const key = clusterOfEvent(ev).key;
  return FOLDED_INTO_OTHER.has(key) ? 'other' : key;
}

export interface HomeEventStats {
  /** 每一格的筆數（**原始事件**，不套用時間軸的相鄰同類合併）。固定含全部的鍵。 */
  counts: Record<HomeEventStatKey, number>;
  /** 非系統事件總數＝各格相加。 */
  total: number;
  /** 被排除的維運/稽核紀錄筆數。全 0 時要靠它分辨「真的沒事」與「只有系統紀錄」。 */
  systemCount: number;
}

/** 計數。輸入是首頁那一批已抓好的事件——本函式不打任何 API。 */
export function countHomeEventStats(events: Pick<EventItem, 'event_id' | 'event_type'>[]): HomeEventStats {
  const counts = Object.fromEntries(HOME_EVENT_STAT_ORDER.map((k) => [k, 0])) as Record<HomeEventStatKey, number>;
  let systemCount = 0;
  for (const ev of events) {
    const key = homeStatKeyOf(ev);
    if (key === null) systemCount += 1;
    else counts[key] += 1;
  }
  const total = HOME_EVENT_STAT_ORDER.reduce((n, k) => n + counts[k], 0);
  return { counts, total, systemCount };
}
